import {
	Injectable,
	ComponentRef,
	ApplicationRef,
	Injector,
	ViewContainerRef
} from '@angular/core';
import { DialogToastComponent } from './dialog-toast/dialog-toast.component';

@Injectable({
	providedIn: 'root'
})
export class ToastService {
	constructor(private appRef: ApplicationRef, private injector: Injector) {}

	// An array to keep track of all active toasts.
	toasts: ComponentRef<DialogToastComponent>[] = [];

	// A map to store the height of each toast.
	toastHeights: Map<ComponentRef<DialogToastComponent>, number> = new Map();

	private viewContainerRef: ViewContainerRef;
	/**
	 * Sets the viewContainerRef from a parent component.
	 * @param viewContainerRef - The ViewContainerRef to use for creating components.
	 */
	setViewContainerRef(viewContainerRef: ViewContainerRef): void {
		this.viewContainerRef = viewContainerRef;
	}

	/**
	 * Closes (removes) a specific toast.
	 * @param toastRef - The reference to the toast component to be closed.
	 */
	closeToast(toastRef: ComponentRef<DialogToastComponent>): void {
		this.removeToast(toastRef);
	}

	/**
	 * Displays a new toast with the given message and state.
	 * Optionally, a callback can be provided to handle confirmations.
	 * @param message - The message to be displayed in the toast.
	 * @param state - The visual state of the toast (default, success, warning, error).
	 * @param handle - Optional callback to handle confirmations.
	 * @returns The reference to the created toast component.
	 */
	showToast(
		message: string,
		state: 'default' | 'success' | 'warning' | 'error' = 'default',
		handle: (confirmed: boolean) => void = null
	): ComponentRef<DialogToastComponent> {
		const toastRef = this.createToast(message, state, handle !== null);
		this.toasts.push(toastRef);

		// Listen for the dismiss event from the toast.
		// If confirmed, execute the provided handle function.
		toastRef.instance.dismiss.subscribe((confirmed: boolean) => {
			if (confirmed && handle) {
				handle(confirmed);
			}
			this.removeToast(toastRef);
		});

		// Reposition all toasts to account for the new one.
		this.repositionToasts();
		return toastRef;
	}

	/**
	 * Creates a new toast component and appends it to the DOM.
	 * @param message - The message to be displayed in the toast.
	 * @param state - The visual state of the toast.
	 * @param showConfirmButton - Whether to show a confirmation button.
	 * @returns The reference to the created toast component.
	 */
	private createToast(
		message: string,
		state: 'default' | 'success' | 'warning' | 'error' = 'default',
		showConfirmButton: boolean = false
	): ComponentRef<DialogToastComponent> {
		// Use ViewContainerRef to create the component.
		const toastRef = this.viewContainerRef.createComponent(DialogToastComponent, {
			index: 0, // Insert at the beginning of the container.
			injector: this.injector
		});

		// Set the toast's properties.
		toastRef.instance.message = message;
		toastRef.instance.state = state;
		toastRef.instance.showConfirmButton = showConfirmButton;

		// Force a change detection cycle to update the view.
		toastRef.instance.cdRef.detectChanges();

		// Calculate and store the toast's height.
		const toastHeight =
			toastRef.location.nativeElement.offsetHeight +
			parseInt(
				window.getComputedStyle(toastRef.location.nativeElement).marginBottom,
				10
			);
		this.toastHeights.set(toastRef, toastHeight);

		// Add a class for the entrance animation.
		setTimeout(() => {
			toastRef.location.nativeElement.classList.add('visible');
		}, 0);

		return toastRef;
	}

	/**
	 * Positions a specific toast based on its index.
	 * Toasts are stacked from the bottom up.
	 * @param toastRef - The reference to the toast component to be positioned.
	 * @param index - The index of the toast in the toasts array.
	 */
	private positionToast(
		toastRef: ComponentRef<DialogToastComponent>,
		index: number
	) {
		const { nativeElement } = toastRef.location;

		// Calculate the cumulative height of all toasts below the current one, including their margins.
		const totalHeightOfToastsBelow = Array.from(this.toastHeights.entries())
			.slice(index + 1) // Get all toasts below the current one.
			.reduce((acc, [ref, height]) => {
				const marginBottom = parseInt(
					window.getComputedStyle(ref.location.nativeElement).marginBottom,
					10
				);
				return acc + height + (marginBottom - 10);
			}, 0);

		// Set the bottom position of the toast.
		nativeElement.style.bottom = `${totalHeightOfToastsBelow}px`;
	}

	/**
	 * Removes a specific toast from the DOM and the toasts array.
	 * @param toastRef - The reference to the toast component to be removed.
	 */
	private removeToast(toastRef: ComponentRef<DialogToastComponent>) {
		if (!this.toasts.includes(toastRef)) {
			return;
		}

		const { nativeElement } = toastRef.location;
		nativeElement.classList.remove('visible');
		this.toasts = this.toasts.filter(t => t !== toastRef);
		this.toastHeights.delete(toastRef);

		// Reposition the remaining toasts.
		this.repositionToasts();

		// Destroy the toast after its exit animation completes.
		setTimeout(() => {
			this.appRef.detachView(toastRef.hostView);
			toastRef.destroy();
		}, 300);
	}

	/**
	 * Repositions all toasts to ensure they are stacked correctly from the bottom up.
	 */
	private repositionToasts() {
		this.toasts.forEach((toastRef, index) => {
			this.positionToast(toastRef, index);
		});
	}
}
